/**
 * Small accessible form controls for the מאזן wizard. Every control has a real
 * <label>, and the one the step validation points at gets aria-invalid +
 * aria-describedby to the visible error (WCAG 3.3.1 / 3.3.3).
 */

import type { ReactNode } from 'react'

interface BaseProps {
  id: string
  label: string
  hint?: string
  invalidId?: string
  errorId?: string
  className?: string
}

const describedBy = (id: string, hint?: string, invalidId?: string, errorId?: string) =>
  [hint ? `${id}-hint` : '', invalidId === id && errorId ? errorId : ''].filter(Boolean).join(' ') || undefined

export function TextField({
  id, label, hint, invalidId, errorId, className, value, onChange, type = 'text', dir, inputMode, placeholder, autoComplete, min, step,
}: BaseProps & {
  value: string | number
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'number'
  dir?: 'ltr' | 'rtl'
  inputMode?: 'numeric' | 'decimal' | 'text'
  placeholder?: string
  autoComplete?: string
  min?: number
  step?: number | string
}) {
  const invalid = invalidId === id
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-800">{label}</label>
      <input
        id={id}
        type={type}
        dir={dir}
        inputMode={inputMode}
        placeholder={placeholder}
        autoComplete={autoComplete}
        min={min}
        step={step}
        className={`input ${invalid ? 'input-error' : ''}`}
        value={value}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(id, hint, invalidId, errorId)}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p id={`${id}-hint`} className="mt-1 text-xs text-gray-600">{hint}</p>}
    </div>
  )
}

export function SelectField({
  id, label, hint, invalidId, errorId, className, value, onChange, options,
}: BaseProps & { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  const invalid = invalidId === id
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-800">{label}</label>
      <select
        id={id}
        className={`input ${invalid ? 'input-error' : ''}`}
        value={value}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(id, hint, invalidId, errorId)}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {hint && <p id={`${id}-hint`} className="mt-1 text-xs text-gray-600">{hint}</p>}
    </div>
  )
}

export function CheckField({ id, label, checked, onChange }: { id: string; label: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start gap-2 text-sm text-gray-800">
      <input id={id} type="checkbox" className="mt-1 h-4 w-4" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <label htmlFor={id}>{label}</label>
    </div>
  )
}

/** A two-or-more option choice rendered as toggle buttons (aria-pressed). */
export function ChoiceButtons<T extends string | boolean>({
  legend, idPrefix, value, options, onChange, invalidId, errorId,
}: {
  legend: string
  idPrefix: string
  value: T | null
  options: { value: T; label: string; id: string }[]
  onChange: (v: T) => void
  invalidId?: string
  errorId?: string
}) {
  const invalid = options.some((o) => o.id === invalidId)
  return (
    <fieldset className="mt-2" aria-describedby={invalid && errorId ? errorId : undefined}>
      <legend className="mb-2 text-sm font-semibold text-gray-900">{legend}</legend>
      <div className="flex flex-wrap gap-2" id={idPrefix}>
        {options.map((o) => (
          <button
            key={o.id}
            id={o.id}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              value === o.value
                ? 'border-emerald-800 bg-emerald-800 text-white'
                : `border-gray-300 bg-white text-gray-800 hover:border-emerald-700 ${invalid ? 'ring-2 ring-red-500' : ''}`
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  )
}
